/**
 * ThemeToggle — bascule clair ⇄ sombre depuis le UI, en un seul clic.
 *
 * @remarks
 * Lit/écrit `useThemeStore`. Toggle binaire : l'icône affichée est celle du
 * thème vers lequel on bascule (lune = passer en sombre, soleil = passer en
 * clair). Le mode `'system'` reste géré par le store et la page Réglages —
 * ici on le résout via `prefers-color-scheme` pour choisir la bascule.
 *
 * **Note d'archi** : on accepte ici qu'un composant de `shared/components/`
 * lit un store de `app/stores/`. C'est un cas où le composant est
 * **transverse** mais doit utiliser un store global. Tolérable car le store
 * est dans `app/` (pas dans une feature) et ce store est par nature
 * partagé. Si ça s'étend, envisager un context/provider à la place.
 */

import { Sun, Moon } from 'lucide-react';
import { useThemeStore } from '@/app/stores/use-theme-store';
import { IconButton } from '@/shared/ui';

function prefersDark() {
  if (typeof window === 'undefined') return false;
  return window.matchMedia('(prefers-color-scheme: dark)').matches;
}

export function ThemeToggle() {
  const theme = useThemeStore((s) => s.theme);
  const setTheme = useThemeStore((s) => s.setTheme);

  const isDark = theme === 'dark' || (theme === 'system' && prefersDark());

  const toggle = () => {
    setTheme(isDark ? 'light' : 'dark');
  };

  const Icon = isDark ? Sun : Moon;
  const label = isDark ? 'Passer en thème clair' : 'Passer en thème sombre';

  return (
    <IconButton aria-label={label} title={label} onClick={toggle} size="sm">
      <Icon className="size-4" aria-hidden />
    </IconButton>
  );
}
